import { DataSource } from "./DataSource";

/**
 * The sort direction.
 */
export type SortDirection = "ascending" | "descending" | null;

/**
 * Specifies the sort methods.
 */
export interface SortMethods {

  /**
   * Sorts the rows by column with the specified name.
   * @param name The name of the column to sort by.
   * @param direction The sort by or null to remove sorting.
   * @param exclusive Specifies whether the sort is exclusive or part of a multi column sort. If not specified defaults to `true`.
   */
  sortBy(name: string, direction: SortDirection | null, exclusive?: boolean): void;

  /**
   * Clears the sorted columns.
   */
  clearSort(): void;
}

/**
 * Specifies properties and methods for the sorting of data source data.
 */
export interface Sortable extends SortMethods {

  /**
   * Indicates whether the data source is currently being sorted.
   */
  readonly sorted: boolean;

  /**
   * Gets the sort direction for the column with the specified name.
   * @param name The name of the column to get the sort direction for.
   * @returns The sort direction for the specified column or null if the column isn't participating in sorting.
   */
  getSortDirection(name: string): SortDirection | null;
}

/**
 * Indicates whether the specified DataSource implements the Sortable interface.
 * @param dataSource The datasource to test.
 * @returns True if the DataSource implements the Sortable interface, otherwise false.
 */
export function isSortable(dataSource: DataSource<object>): dataSource is DataSource<object> & Sortable {
  if (!dataSource) return false;
  return Object.prototype.hasOwnProperty.call(dataSource, "sorted")
  && Object.prototype.hasOwnProperty.call(dataSource, "sortBy")
  && Object.prototype.hasOwnProperty.call(dataSource, "clearSort")
  && Object.prototype.hasOwnProperty.call(dataSource, "getSortDirection");
}
